import { useCallback, useContext, useMemo } from 'react';
import { Modal, Header } from '@trinity/chat-components/components/Modal/index.js';
import type { ModalProps } from '@trinity/chat-components/components/Modal/Modal.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import { TagFormContext, TagStateContext, type TagFormData } from './Context';
import { useCreateTagMutation } from './hooks/useCreateTagMutation';
import { Tags } from './interfaces';
import styles from './style/CreateTagModal.module.css';
import { TagForm } from './TagForm';
import { TagFormContainer } from './TagFormContainer';

const ColorSelectDefault = Tags.defaultColor;

type Props = Pick<ModalProps, 'open' | 'onClose'> & {
  parentTagId: number;
  defaultColor?: string;
};

export const CreateTag = ({ open, onClose, parentTagId, defaultColor = ColorSelectDefault }: Props) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Header>Add New Tag</Header>
      <TagFormContainer defaultState={{ parentId: parentTagId, title: '', color: defaultColor, category: null, includeSummary: true }}>
        <ModalContent onClose={onClose} parentTagId={parentTagId} />
      </TagFormContainer>
    </Modal>
  );
};

type ContentProps = Omit<Props, 'open'>;

const ModalContent = ({ onClose, parentTagId }: ContentProps) => {
  const { state, projectId } = useContext(TagStateContext);
  const { data } = useContext(TagFormContext);

  const canSave = useMemo(() => tagFormIsValid(data), [data]);

  const mutation = useCreateTagMutation(projectId);

  const saveTag = useCallback(() => {
    mutation.mutateAsync({
      tag: {
        baseTag: {
          color: data.color,
          name: data.title,
          id: null,
          tagCategory: data.category,
        },
        parentTagId: data.parentId,
        ordinal: Math.max(0, ...state.tags.concat(state.tags.flatMap(t => t)).map(t => t.ordinal)) + 1,
      },
    }).then(onClose);
  }, [mutation, onClose, state.tags, data]);

  return (
    <>
      <TagForm disableParent={!!parentTagId} />
      <div className={styles.actions}>
        <Button
          variant="brick"
          color="destructive"
          onClick={onClose}>
          Cancel
        </Button>
        <ButtonActivityIndicator
          disabled={!canSave}
          implicitDisable={false}
          loading={mutation.isPending}
          variant="brick"
          onClick={saveTag}>
          Save
        </ButtonActivityIndicator>
      </div>
    </>
  );
};

const tagFormIsValid = (form: TagFormData) => {
  return form.title?.length > 3
    && form.title.length < 64
    && form.title.replace(/\s/g, '')?.length > 3;
};